/**
 * Décodage du JWT Allocab côté front. `GET /v1/users/me` ne renvoie que les
 * accès (`RoleGrant[]`) : l'identité affichée (nom, compte) vient des claims.
 *
 * Le JWT est non signé (`alg: 'none'`) chez Allocab — on décode juste le
 * payload base64url, aucune vérification crypto (l'API back vérifie en amont).
 * Les rôles `Extranet_*` ne sont **pas** dans le JWT (seul le pivot
 * `Extranet_Utilisateur` y figure) : ne pas s'en servir pour le gating.
 */

/** Sous-ensemble des claims qu'on consomme côté extranet. */
export interface AllocabJwtClaims {
  id: string;
  userKey: string;
  userTypeKey?: string;
  accountKey: string;
  userFullName: string;
  type: 'Extranet' | 'Passenger' | 'Driver' | 'BackOffice' | string;
  accountCategory?: string; // 'SNCF' | 'SMB' | 'BP1 Hospitality' | ...
  roles?: string[];
  admin?: boolean;
  allocabAdmin?: boolean;
  kind?: 'CORPORATE' | string;
  expires: number;
  issued: number;
}

const base64UrlDecode = (segment: string): string => {
  const padded = segment + '='.repeat((4 - (segment.length % 4)) % 4);
  const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');
  return atob(base64);
};

export const decodeJwt = (accessToken: string | undefined): AllocabJwtClaims | null => {
  if (!accessToken) return null;
  const parts = accessToken.split('.');
  if (parts.length < 2) return null;
  try {
    return JSON.parse(base64UrlDecode(parts[1])) as AllocabJwtClaims;
  } catch {
    return null;
  }
};

/** Split "Booker SNCF" → { firstName: 'Booker', lastName: 'SNCF' }. */
export const splitFullName = (fullName: string): { firstName: string; lastName: string } => {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
};
